import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { EmptyPanel } from '@/components/feedback/empty-panel'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { FormField } from '@/components/forms/form-field'
import {
  MultiCheckboxSelect,
  type MultiCheckboxSelectOption,
} from '@/components/forms/multi-checkbox-select'
import { SearchInput } from '@/components/forms/search-input'
import { Textarea } from '@/components/forms/textarea'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Drawer } from '@/components/overlays/drawer'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { ApiError } from '@/lib/api/http-client'

import {
  getAdminAppAvailableActions,
  listAdminApps,
  listAdminAppTransitions,
  transitionAdminAppStatus,
  type AdminApp,
  type AdminAppTransitionAction,
} from './api'
import { AdminAppLifecycleProgress, AdminAppTransitionHistory } from './lifecycle-display'
import { formatDateTime, translateStatus } from './format'
import { AdminAppNameCell, AdminAppStatusCell } from './shared'
import { adminAppStatusFilterOrder } from './status'

const pageSizeOptions = [10, 20, 50]
const defaultReviewStatuses = adminAppStatusFilterOrder.filter((status) => status !== 'active')
const defaultAdminAppReviewColumnIds = [
  'app',
  'status',
  'change-type',
  'app-type',
  'version',
  'updated-at',
  'actions',
]

type AdminAppReviewSortField = 'name' | 'updated_at'
type AdminAppReviewSortOrder = 'asc' | 'desc'

const actionTone: Record<AdminAppTransitionAction, 'danger' | 'primary' | 'secondary'> = {
  approve_for_testing: 'primary',
  ban: 'danger',
  publish: 'primary',
  republish: 'primary',
  request_publish: 'primary',
  return_to_draft: 'danger',
  return_to_testing: 'danger',
}

const actionPriority: Record<AdminAppTransitionAction, number> = {
  return_to_draft: 10,
  return_to_testing: 10,
  approve_for_testing: 20,
  request_publish: 20,
  publish: 20,
  republish: 20,
  ban: 30,
}

function reviewTypeForStatus(status: string, t: TFunction<'adminApps'>) {
  const key = `review.changeTypes.${status}`
  const translated = t(key)

  return translated === key ? '-' : translated
}

function transitionErrorMessage(
  error: unknown,
  fallback: string,
  detailTemplate: (detail: string) => string,
) {
  if (error instanceof ApiError && error.message.trim().length > 0) {
    return detailTemplate(error.message)
  }

  return fallback
}

function AdminAppReviewDrawer({
  app,
  onOpenChange,
  open,
}: {
  app: AdminApp
  onOpenChange: (open: boolean) => void
  open: boolean
}) {
  const { t } = useTranslation('adminApps')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const commentId = useId()
  const [comment, setComment] = useState('')

  const actionsQuery = useQuery({
    enabled: open,
    queryFn: () => getAdminAppAvailableActions(app.id),
    queryKey: ['admin-app-review-actions', app.id],
  })

  const transitionsQuery = useQuery({
    enabled: open,
    queryFn: () => listAdminAppTransitions(app.id),
    queryKey: ['admin-app-review-transitions', app.id],
  })

  const transitionMutation = useMutation({
    mutationFn: (action: AdminAppTransitionAction) =>
      transitionAdminAppStatus({
        action,
        appId: app.id,
        comment,
      }),
    onSuccess: async () => {
      setComment('')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-apps'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-app-review-actions', app.id] }),
        queryClient.invalidateQueries({ queryKey: ['admin-app-review-transitions', app.id] }),
      ])
      showFeedback({
        message: t('review.drawer.transitionSuccess'),
        scope: 'global',
        tone: 'success',
      })
      onOpenChange(false)
    },
  })

  const availableActions = [...(actionsQuery.data ?? [])]
    .filter((action) => action !== 'ban')
    .sort((left, right) => actionPriority[left] - actionPriority[right])
  const errorMessage = transitionMutation.isError
    ? transitionErrorMessage(transitionMutation.error, t('review.drawer.submitFailed'), (detail) =>
        t('review.drawer.submitFailedWithDetail', { detail }),
      )
    : null
  const showComment = actionsQuery.isLoading || availableActions.length > 0

  return (
    <Drawer
      description={translateStatus(t, app.status)}
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-3">
          <Button onClick={() => onOpenChange(false)} variant="secondary">
            {t('review.drawer.close')}
          </Button>
          {actionsQuery.isLoading ? (
            <span
              aria-label={t('review.drawer.availableActions')}
              className="h-10 w-24 animate-pulse rounded-md bg-[var(--color-skeleton)]"
              role="status"
            />
          ) : (
            availableActions.map((action) => (
              <Button
                disabled={transitionMutation.isPending}
                isLoading={transitionMutation.isPending && transitionMutation.variables === action}
                key={action}
                onClick={() => transitionMutation.mutate(action)}
                variant={actionTone[action] === 'danger' ? 'danger' : actionTone[action]}
              >
                {t(`review.actions.${action}`)}
              </Button>
            ))
          )}
        </div>
      }
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={t('review.drawer.titleWithName', { name: app.name })}
    >
      <div className="grid gap-6">
        <section className="grid gap-3 rounded-lg border border-[var(--color-panel-border)] bg-[#fbfbfb] p-4">
          <AdminAppNameCell
            app={app}
            subtitle={app.description || t('review.drawer.noDescription')}
          />
          <AdminAppLifecycleProgress status={app.status} t={t} />
        </section>

        {actionsQuery.isError ? (
          <InlineNotice tone="danger">{t('review.drawer.actionsLoadFailed')}</InlineNotice>
        ) : null}

        {!actionsQuery.isLoading && !actionsQuery.isError && availableActions.length === 0 ? (
          <EmptyPanel className="py-4">
            {app.status === 'active'
              ? t('review.drawer.activeDirectTip')
              : t('review.drawer.noActions')}
          </EmptyPanel>
        ) : null}

        {showComment ? (
          <FormField
            hint={t('review.drawer.commentHint')}
            htmlFor={commentId}
            label={t('review.drawer.commentLabel')}
          >
            <Textarea
              disabled={actionsQuery.isLoading || transitionMutation.isPending}
              id={commentId}
              maxLength={500}
              onChange={(event) => setComment(event.target.value)}
              placeholder={t('review.drawer.commentPlaceholder')}
              value={comment}
            />
          </FormField>
        ) : null}

        {errorMessage ? (
          <InlineNotice role="alert" tone="danger">
            {errorMessage}
          </InlineNotice>
        ) : null}

        <section className="grid gap-3">
          <h3 className="text-sm font-semibold text-[var(--color-text)]">
            {t('review.drawer.history')}
          </h3>
          {transitionsQuery.isError ? (
            <InlineNotice tone="danger">{t('review.drawer.historyLoadFailed')}</InlineNotice>
          ) : transitionsQuery.isLoading ? (
            <div className="grid gap-2">
              {Array.from({ length: 3 }, (_, index) => (
                <div
                  className="h-[74px] animate-pulse rounded-lg bg-[var(--color-skeleton)]"
                  key={index}
                />
              ))}
            </div>
          ) : (
            <AdminAppTransitionHistory items={transitionsQuery.data ?? []} t={t} />
          )}
        </section>
      </div>
    </Drawer>
  )
}

function DirectUnpublishDialog({ app, onClose }: { app: AdminApp; onClose: () => void }) {
  const { t } = useTranslation('adminApps')
  const commentId = useId()
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const [comment, setComment] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      transitionAdminAppStatus({
        action: 'ban',
        appId: app.id,
        comment,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-apps'] })
      showFeedback({
        message: t('review.directUnpublish.success'),
        scope: 'global',
        tone: 'success',
      })
      onClose()
    },
  })

  const errorMessage = mutation.isError
    ? transitionErrorMessage(mutation.error, t('review.directUnpublish.submitFailed'), (detail) =>
        t('review.directUnpublish.submitFailedWithDetail', { detail }),
      )
    : null

  return (
    <Modal
      closeLabel={t('review.directUnpublish.close')}
      footer={
        <>
          <Button disabled={mutation.isPending} onClick={onClose} variant="secondary">
            {t('review.directUnpublish.cancel')}
          </Button>
          <Button isLoading={mutation.isPending} onClick={() => mutation.mutate()} variant="danger">
            {t('review.directUnpublish.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open
      subtitle={t('review.directUnpublish.subtitle', { name: app.name })}
      title={t('review.directUnpublish.title')}
    >
      <div className="grid gap-4">
        <p className="text-sm leading-6 text-[var(--color-text-muted)]">
          {t('review.directUnpublish.description')}
        </p>
        <FormField
          hint={t('review.directUnpublish.commentHint')}
          htmlFor={commentId}
          label={t('review.directUnpublish.commentLabel')}
        >
          <Textarea
            disabled={mutation.isPending}
            id={commentId}
            maxLength={500}
            onChange={(event) => setComment(event.target.value)}
            placeholder={t('review.directUnpublish.commentPlaceholder')}
            value={comment}
          />
        </FormField>
        {errorMessage ? (
          <InlineNotice role="alert" tone="danger">
            {errorMessage}
          </InlineNotice>
        ) : null}
      </div>
    </Modal>
  )
}

export function AdminAppReviewPage() {
  const { t } = useTranslation('adminApps')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statuses, setStatuses] = useState<string[]>(defaultReviewStatuses)
  const [sortField, setSortField] = useState<AdminAppReviewSortField>('name')
  const [sortOrder, setSortOrder] = useState<AdminAppReviewSortOrder>('desc')
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(defaultAdminAppReviewColumnIds)
  const [reviewApp, setReviewApp] = useState<AdminApp | null>(null)
  const [unpublishApp, setUnpublishApp] = useState<AdminApp | null>(null)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 250)

    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const appsQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listAdminApps({
        order: sortOrder,
        orderBy: sortField,
        page,
        pageSize,
        search,
        status: statuses,
      }),
    queryKey: ['admin-apps', 'review', { page, pageSize, search, sortField, sortOrder, statuses }],
  })

  const statusOptions: MultiCheckboxSelectOption[] = useMemo(
    () =>
      adminAppStatusFilterOrder.map((status) => ({
        label: t(`status.${status}`),
        value: status,
      })),
    [t],
  )

  const visibleColumnIdSet = useMemo(() => new Set(visibleColumnIds), [visibleColumnIds])
  const toggleSort = useCallback(
    (field: AdminAppReviewSortField) => {
      const nextOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc'

      setSortField(field)
      setSortOrder(nextOrder)
      setPage(1)
    },
    [sortField, sortOrder],
  )
  const columns = useMemo<DataTableColumn<AdminApp>[]>(() => {
    const sortDirection = (field: AdminAppReviewSortField) =>
      sortField === field ? sortOrder : null
    const sortActionLabel = (field: AdminAppReviewSortField, ascKey: string, descKey: string) =>
      sortField === field && sortOrder === 'desc' ? t(ascKey) : t(descKey)

    return [
      {
        header: t('review.table.app'),
        headerAlign: 'center',
        hidden: !visibleColumnIdSet.has('app'),
        id: 'app',
        pin: 'left',
        render: (app) => (
          <AdminAppNameCell app={app} subtitle={app.description || t('emptyDescription')} />
        ),
        visibilityLabel: t('review.table.app'),
        width: 300,
      },
      {
        header: t('review.table.status'),
        hidden: !visibleColumnIdSet.has('status'),
        id: 'status',
        render: (app) => (
          <AdminAppStatusCell status={app.status} text={translateStatus(t, app.status)} />
        ),
        visibilityLabel: t('review.table.status'),
        width: 130,
      },
      {
        header: t('review.table.changeType'),
        hidden: !visibleColumnIdSet.has('change-type'),
        id: 'change-type',
        render: (app) => reviewTypeForStatus(app.status, t),
        visibilityLabel: t('review.table.changeType'),
        width: 150,
      },
      {
        header: t('review.table.appType'),
        hidden: !visibleColumnIdSet.has('app-type'),
        id: 'app-type',
        render: (app) => app.app_type.replaceAll('_', ' '),
        visibilityLabel: t('review.table.appType'),
        width: 140,
      },
      {
        header: t('review.table.version'),
        hidden: !visibleColumnIdSet.has('version'),
        id: 'version',
        render: (app) => app.version ?? '-',
        visibilityLabel: t('review.table.version'),
        width: 120,
      },
      {
        header: t('review.table.updatedAt'),
        hidden: !visibleColumnIdSet.has('updated-at'),
        id: 'updated-at',
        render: (app) => formatDateTime(app.updated_at),
        sort: {
          direction: sortDirection('updated_at'),
          label: sortActionLabel('updated_at', 'sort.updatedAtAsc', 'sort.updatedAtDesc'),
          onToggle: () => toggleSort('updated_at'),
        },
        visibilityLabel: t('review.table.updatedAt'),
        width: 190,
      },
      {
        align: 'center',
        header: t('review.table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (app) => (
          <ActionMenu
            ariaLabel={t('review.actions.open', { name: app.name })}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'review',
                label:
                  app.status === 'active'
                    ? t('review.actions.viewHistory')
                    : t('review.actions.process'),
                onSelect: () => setReviewApp(app),
              },
              ...(app.status === 'active'
                ? [
                    {
                      iconSrc: actionDeleteIconUrl,
                      key: 'unpublish',
                      label: t('review.actions.unpublish'),
                      onSelect: () => setUnpublishApp(app),
                      variant: 'danger' as const,
                    },
                  ]
                : []),
            ]}
          />
        ),
        visibilityLabel: t('review.table.actions'),
        width: 96,
      },
    ]
  }, [sortField, sortOrder, t, toggleSort, visibleColumnIdSet])

  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  const listData = appsQuery.data
  const apps = listData?.items ?? []
  const total = listData?.total ?? 0
  const showError = appsQuery.isError && !appsQuery.data
  const errorDescription =
    appsQuery.error instanceof ApiError
      ? t('errorDescriptionWithDetail', { detail: appsQuery.error.message })
      : t('review.errorDescription')

  return (
    <AdminShellPage activePath="/admin/app-review">
      <section
        aria-label={t('review.workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-app-review-workspace"
      >
        <PageHeader
          actions={
            <DataTableColumnSelector
              aria-label={t('review.columnSelector.label')}
              onValueChange={setVisibleColumnIds}
              options={columnSelectorOptions}
              value={visibleColumnIds}
            >
              {t('review.columnSelector.trigger')}
            </DataTableColumnSelector>
          }
          description={t('review.description')}
          title={t('review.title')}
        />
        <FilterBar>
          <SearchInput
            aria-label={t('filters.searchLabel')}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            value={searchInput}
          />
          <MultiCheckboxSelect
            aria-label={t('filters.statusLabel')}
            allLabel={t('filters.allStatuses')}
            onValueChange={(value) => {
              setStatuses(value)
              setPage(1)
            }}
            options={statusOptions}
            value={statuses}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 lg:px-[30px]">
          {showError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => void appsQuery.refetch()}
                  size="sm"
                  variant="secondary"
                >
                  {t('retry')}
                </Button>
              }
              description={errorDescription}
              title={t('review.errorTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('review.empty')}
                getRowKey={(app) => app.id}
                isLoading={appsQuery.isLoading}
                rows={apps}
              />
              <Pagination
                disabled={appsQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize)
                  setPage(1)
                }}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                total={total}
              />
            </>
          )}
        </div>
        {reviewApp ? (
          <AdminAppReviewDrawer
            app={reviewApp}
            key={reviewApp.id}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) {
                setReviewApp(null)
              }
            }}
            open
          />
        ) : null}
        {unpublishApp ? (
          <DirectUnpublishDialog
            app={unpublishApp}
            key={unpublishApp.id}
            onClose={() => setUnpublishApp(null)}
          />
        ) : null}
      </section>
    </AdminShellPage>
  )
}
